/**
 * The print destination — a separate browser window holding ONE self-contained
 * document.
 *
 * This replaces the `#print-root` scheme (#1902): a portal into a static node
 * beside `#root`, a `@media print` rule hiding every other body child, a
 * re-declaration of the light theme tokens so a dark theme could not print
 * white-on-white, a `.no-print` class for the preview toolbar, and an
 * architecture test holding all four in place. Every one of those existed
 * only because the sheet shared a document with the app. A separate document
 * has no app in it, so none of them has anything left to protect.
 *
 * It is also the shape the retired server-side renderer had: `packagingToHtml`
 * returned `<!doctype html>` plus its own `<style>`, and the operator printed
 * it from the browser's own dialog. The colours #1902 lists went wrong in the
 * first place because the React rewrite inherited the app's tokens; here there
 * are none to inherit.
 *
 * TWO rules govern this file:
 *
 *  1. `window.open` must run in the user gesture's synchronous call stack.
 *     Every browser blocks a popup opened after an `await`, so the caller
 *     opens FIRST and fetches second — see `usePrintJob`.
 *  2. Nothing here may reference the app's stylesheet, its tokens, or its
 *     components. What the printer receives is exactly what this document
 *     says.
 */

/** The page geometry and the strings the shell shows before the sheet lands. */
export interface PrintDocumentInit {
  /** `document.title` — what the window's tab and the print header show. */
  title: string;
  /** BCP-47 tag for `<html lang>`; taken from the running i18n language. */
  lang: string;
  /** `@page` descriptors. */
  page: { size: string; margin: string };
  /** Screen-only note about the browser's own headers/footers setting. */
  hint: string;
  /** Shown until the sheet resolves — or replaced by the failure. */
  status: string;
}

export interface PrintDocument {
  /** The opened window. The caller listens on it and closes it. */
  readonly window: Window;
  /** The node the sheet is rendered into, by portal. */
  readonly mount: HTMLElement;
  /** Replace the status line; `null` removes it (the sheet has landed). */
  setStatus: (text: string | null) => void;
  /** Close the window. Safe to call on an already-closed one. */
  close: () => void;
}

/**
 * The document-level stylesheet. Everything a printed sheet can rely on
 * without shipping CSS of its own; anything a particular sheet needs beyond
 * this it renders itself, as a `<style>` in its own subtree.
 *
 * `print-color-adjust: exact` is not optional: browsers drop backgrounds when
 * printing, and the driver pill and the card cut lines ARE backgrounds.
 */
export function printDocumentStylesheet(page: { size: string; margin: string }): string {
  return `@page { size: ${page.size}; margin: ${page.margin}; }
* {
  box-sizing: border-box;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
html, body { margin: 0; padding: 0; color: #111; }
body { font-family: system-ui, sans-serif; }
.print-hint {
  margin: 8px auto;
  padding: 8px 12px;
  max-width: 207mm;
  background: #fff7cc;
  border: 1px solid #d6b700;
  font-size: 14px;
}
.print-status { margin: 24px; font-size: 14px; }
@media print { .print-hint, .print-status { display: none; } }`;
}

/**
 * Open the print window and write its shell. Returns `null` — never throws —
 * when the popup was blocked, which is a routine outcome the caller reports
 * to the operator.
 */
export function openPrintDocument(init: PrintDocumentInit): PrintDocument | null {
  // FIRST statement, deliberately: see rule 1 in the file header.
  const win = window.open('', '_blank');
  if (win === null) return null;

  const doc = win.document;
  doc.documentElement.lang = init.lang;
  doc.title = init.title;

  const style = doc.createElement('style');
  style.textContent = printDocumentStylesheet(init.page);
  doc.head.append(style);

  const hint = doc.createElement('p');
  hint.className = 'print-hint';
  hint.textContent = init.hint;
  doc.body.append(hint);

  const status = doc.createElement('p');
  status.className = 'print-status';
  status.textContent = init.status;
  doc.body.append(status);

  const mount = doc.createElement('div');
  mount.className = 'print-mount';
  doc.body.append(mount);

  return {
    window: win,
    mount,
    setStatus: (text) => {
      if (text === null) {
        status.remove();
        return;
      }
      status.textContent = text;
      // Re-attach after a clear, so a failure that follows a resolved
      // sheet still has somewhere to appear.
      if (status.parentNode === null) doc.body.insertBefore(status, mount);
    },
    close: () => win.close(),
  };
}
